import SEO from '@/components/common/SEO';
import { Breadcrumb } from '@/components/common/Breadcrumb';
import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  ArrowRight,
  Award,
  Users,
  Target,
  Shield,
  CheckCircle,
  Globe,
  BookOpen,
  Star,
  Sparkles,
  Play,
  TrendingUp,
  Heart,
  Zap,
  Building2,
  GraduationCap,
  Clock,
  Medal,
  ChevronRight,
} from 'lucide-react';

const IMAGES = {
  teamMeeting: "https://static.prod-images.emergentagent.com/jobs/18feaf06-f8ff-4d33-b50d-339f9b1ed281/images/e506e764e32c7baf6932f8bb9e93691156228c95f865466dc0c15f86a10f740c.png",
  officeBuilding: "https://static.prod-images.emergentagent.com/jobs/18feaf06-f8ff-4d33-b50d-339f9b1ed281/images/71d0a3b236844d2d4f70da960dc61a91c04ba4194bcc3a957d5b8fb2d3d62694.png",
  collaboration: "https://static.prod-images.emergentagent.com/jobs/18feaf06-f8ff-4d33-b50d-339f9b1ed281/images/483e359c55caca4723a3ceada6ed56e230b3b18bc94bd84933780c1af121a84c.png",
  speaker: "https://static.prod-images.emergentagent.com/jobs/18feaf06-f8ff-4d33-b50d-339f9b1ed281/images/f9a44555869f7a8c3c96567faf6a54bda4cff4e1514f115eb328e8617e60d26e.png",
};

export default function AboutPage() {
  const values = [
    {
      icon: Award,
      title: 'Excellence',
      description: 'We deliver cutting-edge compliance education with the latest regulatory insights and industry best practices.',
      color: 'from-gold-400 to-gold-600',
    },
    {
      icon: Shield,
      title: 'Integrity',
      description: 'We uphold the highest ethical standards, ensuring accurate, unbiased, and trustworthy content in every program.',
      color: 'from-navy-500 to-navy-700',
    },
    {
      icon: Users,
      title: 'Community',
      description: 'We foster a thriving network of GRC professionals committed to excellence and continuous growth.',
      color: 'from-emerald-500 to-emerald-700',
    },
    {
      icon: Target,
      title: 'Innovation',
      description: 'We continuously evolve our platform and content to meet the changing needs of compliance professionals.',
      color: 'from-purple-500 to-purple-700',
    },
  ];

  const stats = [
    { value: '15K+', label: 'Professionals Trained', icon: GraduationCap },
    { value: '100+', label: 'Expert-Led Webinars', icon: Play },
    { value: '50+', label: 'Industry Speakers', icon: Users },
    { value: '10+', label: 'Industries Served', icon: Building2 },
  ];

  const industries = [
    'Life Sciences & Pharmaceuticals',
    'Banking & Financial Services',
    'Insurance & Risk Management',
    'Healthcare & Medical Devices',
    'Technology & Manufacturing',
    'Trade & Logistics',
    'Construction & Engineering',
  ];

  const specializations = [
    { name: 'Risk Management', icon: Shield },
    { name: 'Quality Assurance', icon: CheckCircle },
    { name: 'Corporate Governance', icon: Building2 },
    { name: 'IT & PCI Compliance', icon: Zap },
    { name: 'HR Compliance', icon: Users },
    { name: 'OSHA & Safety', icon: Heart },
    { name: 'Import/Export', icon: Globe },
    { name: 'Accounting & Tax', icon: TrendingUp },
  ];

  const milestones = [
    { year: '2015', title: 'Founded', description: 'GRC Educators was established with a vision to democratize compliance education.' },
    { year: '2018', title: '5,000 Members', description: 'Reached our first major milestone of 5,000 trained professionals.' },
    { year: '2020', title: 'Digital Expansion', description: 'Launched our comprehensive virtual training platform.' },
    { year: '2023', title: '15K+ Community', description: 'Grew to serve over 15,000 GRC professionals worldwide.' },
    { year: '2025', title: 'Global Reach', description: 'Expanded operations across North America and beyond.' },
  ];

  return (
    <div className="min-h-screen bg-transparent" data-testid="about-page">
      <SEO
        title="About Us"
        description="GRC Educators is a leading provider of governance, risk and compliance training — expert instructors, accredited CPE/CE credits, and thousands of professionals trained across regulated industries."
        path="/about"
      />
      {/* Hero Section - MailCraft light editorial */}
      <section className="relative hero-canvas border-b border-zinc-200 pt-28 pb-24 overflow-hidden">
        <div className="container-custom relative z-10">
          <Breadcrumb />
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <span className="mono-label inline-flex items-center gap-2 text-[11px] text-zinc-600 bg-white border border-zinc-200 rounded-full px-3 py-1 mb-6">
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
                About GRC Educators
              </span>
              <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl font-bold tracking-[-0.03em] leading-[1.0] mb-6">
                <span className="text-zinc-950">Empowering GRC </span>
                <span className="text-slate-400">excellence.</span>
              </h1>
              <p className="text-zinc-500 text-lg md:text-xl leading-relaxed mb-8">
                We are the leading provider of Governance, Risk, and Compliance education, 
                dedicated to empowering professionals with the knowledge and skills needed 
                to navigate today's complex regulatory landscape.
              </p>
              <div className="flex flex-wrap gap-3">
                <Link to="/webinars">
                  <Button className="bg-zinc-950 hover:bg-zinc-800 text-white font-semibold rounded-lg px-6 h-12 transition-colors">
                    Explore Webinars
                    <ArrowRight className="ml-2 w-4 h-4" />
                  </Button>
                </Link>
                <Link to="/contact">
                  <Button variant="outline" className="border border-zinc-300 bg-white text-zinc-900 hover:bg-zinc-50 rounded-lg px-6 h-12">
                    Contact Us
                  </Button>
                </Link>
              </div>
            </div>
            <div className="relative">
              <div className="relative rounded-2xl overflow-hidden shadow-2xl transform hover:scale-[1.02] transition-transform duration-500">
                <img
                  src={IMAGES.teamMeeting}
                  alt="GRC Educators Team"
                  className="w-full h-auto"
                />
                <div className="absolute inset-0 bg-zinc-900/30" />
              </div>
              {/* Floating Stats Card */}
              <div className="absolute -bottom-6 -left-6 bg-white rounded-2xl shadow-xl p-6 animate-fade-in-up">
                <div className="flex items-center gap-4">
                  <div className="w-14 h-14 rounded-xl bg-gold-500 flex items-center justify-center">
                    <Medal className="w-7 h-7 text-navy-800" />
                  </div>
                  <div>
                    <p className="text-3xl font-bold text-navy-700">10+</p>
                    <p className="text-slate-500 text-sm">Years of Excellence</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-16 bg-slate-50 border-y border-slate-200">
        <div className="container-custom">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((stat, idx) => (
              <div 
                key={idx} 
                className="text-center group"
              >
                <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-white shadow-lg flex items-center justify-center group-hover:shadow-xl group-hover:scale-110 transition-all duration-300">
                  <stat.icon className="w-8 h-8 text-gold-500" />
                </div>
                <p className="text-3xl md:text-4xl font-bold text-navy-700 mb-1">{stat.value}</p>
                <p className="text-slate-500 text-sm">{stat.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Mission & Vision */}
      <section className="py-20">
        <div className="container-custom">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div className="order-2 lg:order-1">
              <div className="relative">
                <img
                  src={IMAGES.collaboration}
                  alt="Collaboration"
                  className="rounded-2xl shadow-xl w-full"
                />
                <div className="absolute -bottom-6 -right-6 w-48 h-48 bg-gold-500 rounded-2xl -z-10" />
              </div>
            </div>
            <div className="order-1 lg:order-2">
              <Badge className="bg-navy-100 text-navy-700 mb-4">Our Mission</Badge>
              <h2 className="font-heading text-3xl md:text-4xl font-bold text-navy-700 mb-6">
                Transforming Compliance Education
              </h2>
              <p className="text-slate-600 text-lg leading-relaxed mb-6">
                At GRC Educators, we believe that effective compliance is the cornerstone of 
                successful organizations. Our mission is to provide accessible, high-quality 
                education that empowers professionals to excel in governance, risk management, 
                and compliance roles.
              </p>
              <p className="text-slate-600 text-lg leading-relaxed mb-8">
                We deliver advanced webinars featuring up-to-date information on regulatory 
                changes, industry best practices, risk management strategies, and emerging 
                market trends. Our programs are designed to help you stay ahead in an 
                ever-evolving compliance landscape.
              </p>
              <div className="space-y-4">
                {['Expert-led live and recorded sessions', 'NASBA-accredited CPE credits', 'Practical, actionable insights'].map((item, idx) => (
                  <div key={idx} className="flex items-center gap-3">
                    <div className="w-6 h-6 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0">
                      <CheckCircle className="w-4 h-4 text-green-600" />
                    </div>
                    <span className="text-slate-700">{item}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Core Values */}
      <section className="py-20 bg-slate-50">
        <div className="container-custom">
          <div className="text-center max-w-2xl mx-auto mb-16">
            <Badge className="bg-gold-100 text-gold-700 mb-4">Our Values</Badge>
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-navy-700 mb-4">
              What Drives Us Forward
            </h2>
            <p className="text-slate-600">
              Our core values guide everything we do, from content creation to customer service.
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {values.map((value, idx) => (
              <Card 
                key={idx}
                className="group border-0 shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-2 overflow-hidden"
              >
                <CardContent className="p-8">
                  <div className={`w-14 h-14 rounded-2xl bg-navy-600 flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300`}>
                    <value.icon className="w-7 h-7 text-white" />
                  </div>
                  <h3 className="font-heading text-xl font-semibold text-navy-700 mb-3">
                    {value.title}
                  </h3>
                  <p className="text-slate-600 leading-relaxed">
                    {value.description}
                  </p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Industries & Specializations */}
      <section className="py-20">
        <div className="container-custom">
          <div className="grid lg:grid-cols-2 gap-16">
            {/* Industries */}
            <div>
              <Badge className="bg-navy-100 text-navy-700 mb-4">Industries We Serve</Badge>
              <h2 className="font-heading text-3xl font-bold text-navy-700 mb-6">
                Cross-Industry Expertise
              </h2>
              <p className="text-slate-600 mb-8">
                Our compliance training programs cater to professionals across diverse sectors, 
                each with unique regulatory requirements and challenges.
              </p>
              <div className="space-y-3">
                {industries.map((industry, idx) => (
                  <div 
                    key={idx}
                    className="flex items-center gap-4 p-4 bg-slate-50 rounded-xl hover:bg-navy-50 hover:shadow-md transition-all duration-300 cursor-pointer group"
                  >
                    <div className="w-10 h-10 rounded-xl bg-white shadow-sm flex items-center justify-center group-hover:bg-navy-100 transition-colors">
                      <Building2 className="w-5 h-5 text-navy-600" />
                    </div>
                    <span className="font-medium text-slate-700 group-hover:text-navy-700">{industry}</span>
                    <ChevronRight className="w-4 h-4 text-slate-400 ml-auto group-hover:text-navy-500 group-hover:translate-x-1 transition-all" />
                  </div>
                ))}
              </div>
            </div>

            {/* Specializations */}
            <div>
              <Badge className="bg-gold-100 text-gold-700 mb-4">Specializations</Badge>
              <h2 className="font-heading text-3xl font-bold text-navy-700 mb-6">
                Areas of Focus
              </h2>
              <p className="text-slate-600 mb-8">
                Our expert speakers cover a comprehensive range of compliance topics to 
                meet your professional development needs.
              </p>
              <div className="grid grid-cols-2 gap-4">
                {specializations.map((spec, idx) => (
                  <div 
                    key={idx}
                    className="flex items-center gap-3 p-4 bg-white rounded-xl border border-slate-100 hover:border-gold-200 hover:shadow-md transition-all duration-300 group"
                  >
                    <div className="w-10 h-10 rounded-lg bg-gold-100 flex items-center justify-center group-hover:bg-gold-200 transition-colors">
                      <spec.icon className="w-5 h-5 text-gold-600" />
                    </div>
                    <span className="font-medium text-slate-700 text-sm">{spec.name}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Timeline/Milestones */}
      <section className="py-20 bg-navy-900 text-white">
        <div className="container-custom">
          <div className="text-center max-w-2xl mx-auto mb-16">
            <Badge className="bg-gold-500/20 text-gold-300 border border-gold-500/30 mb-4">
              Our Journey
            </Badge>
            <h2 className="font-heading text-3xl md:text-4xl font-bold mb-4">
              A Decade of Excellence
            </h2>
            <p className="text-slate-400">
              From humble beginnings to industry leadership, our journey has been defined by 
              continuous growth and unwavering commitment to quality.
            </p>
          </div>
          <div className="relative">
            {/* Timeline Line */}
            <div className="absolute left-1/2 transform -translate-x-1/2 h-full w-0.5 bg-gold-500/30 hidden md:block" />
            
            <div className="space-y-12">
              {milestones.map((milestone, idx) => (
                <div 
                  key={idx}
                  className={`flex flex-col md:flex-row items-center gap-8 ${idx % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'}`}
                >
                  <div className={`flex-1 ${idx % 2 === 0 ? 'md:text-right' : 'md:text-left'}`}>
                    <div className="bg-navy-800/50 rounded-2xl p-6 hover:bg-navy-800 transition-colors">
                      <span className="text-gold-400 font-bold text-lg">{milestone.year}</span>
                      <h3 className="font-heading text-xl font-semibold mt-2 mb-2">{milestone.title}</h3>
                      <p className="text-slate-400">{milestone.description}</p>
                    </div>
                  </div>
                  <div className="w-4 h-4 rounded-full bg-gold-500 ring-4 ring-gold-500/30 z-10" />
                  <div className="flex-1 hidden md:block" />
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Speaker Highlight */}
      <section className="py-20">
        <div className="container-custom">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <Badge className="bg-gold-100 text-gold-700 mb-4">Expert Speakers</Badge>
              <h2 className="font-heading text-3xl md:text-4xl font-bold text-navy-700 mb-6">
                Learn From Industry Leaders
              </h2>
              <p className="text-slate-600 text-lg leading-relaxed mb-6">
                Our webinars and seminars are led by recognized experts with decades of 
                real-world experience in governance, risk, and compliance. They bring 
                practical insights and actionable strategies to every session.
              </p>
              <div className="space-y-4 mb-8">
                <div className="flex items-center gap-3">
                  <CheckCircle className="w-5 h-5 text-green-500" />
                  <span className="text-slate-700">50+ industry-recognized experts</span>
                </div>
                <div className="flex items-center gap-3">
                  <CheckCircle className="w-5 h-5 text-green-500" />
                  <span className="text-slate-700">Average 20+ years of experience</span>
                </div>
                <div className="flex items-center gap-3">
                  <CheckCircle className="w-5 h-5 text-green-500" />
                  <span className="text-slate-700">Practitioners from Fortune 500 companies</span>
                </div>
              </div>
              <div className="flex flex-wrap gap-4">
                <Link to="/webinars">
                  <Button className="bg-navy-700 hover:bg-navy-800 text-white rounded-xl px-6">
                    Browse Webinars
                    <ArrowRight className="ml-2 w-4 h-4" />
                  </Button>
                </Link>
                <Link to="/join-speaker">
                  <Button variant="outline" className="border-navy-200 text-navy-700 hover:bg-navy-50 rounded-xl px-6">
                    Join as Speaker
                  </Button>
                </Link>
              </div>
            </div>
            <div className="relative">
              <img
                src={IMAGES.speaker}
                alt="Expert Speaker"
                className="rounded-2xl shadow-xl w-full"
              />
              <div className="absolute -top-6 -right-6 w-32 h-32 bg-navy-600 rounded-2xl -z-10" />
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-[#09090b] text-white border-t border-zinc-800">
        <div className="container-custom">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-white mb-6">
              Ready to Advance Your Compliance Career?
            </h2>
            <p className="text-slate-300 text-lg mb-8">
              Join thousands of professionals who trust GRC Educators for their 
              compliance training needs. Start your journey today.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <Link to="/register">
                <Button className="bg-white hover:bg-zinc-100 text-zinc-950 font-semibold rounded-lg px-8 h-14 transition-colors">
                  <Sparkles className="mr-2 w-5 h-5" />
                  Get Started Free
                </Button>
              </Link>
              <Link to="/webinars">
                <Button variant="outline" className="border-white/30 text-white hover:bg-white/10 rounded-xl px-8 h-14">
                  Browse Webinars
                  <ArrowRight className="ml-2 w-4 h-4" />
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
